/** @format */

import { useEffect, useState } from "react";
import { APP_VERSION, SW_UPDATE_AVAILABLE_EVENT } from "@/constants/version";
import {
  getSwRegistration,
  hasWaitingServiceWorker,
} from "@/utils/serviceWorkerHelpers";
import { useConfig } from "./configApi";
import { useServerVersion as useLiveServerVersion } from "./runsApi";
import { useUser } from "./userAPI";

/** Matches bumpver calver format: YYYY.MM.DD or YYYY.MM.DD.PATCH */
const CALVER_RE = /^\d{4}\.\d{2}\.\d{2}(?:\.\d+)?$/;

/** Parse a calver string into a comparable numeric tuple. Returns null if invalid. */
function parseCalver(v: string): number[] | null {
  if (!CALVER_RE.test(v)) return null;
  return v.split(".").map(Number);
}

/** Returns true if `deployed` is a valid calver AND strictly newer than `local`. */
function isNewerVersion(deployed: string, local: string): boolean {
  const d = parseCalver(deployed);
  const l = parseCalver(local);
  if (!d || !l) return false;
  for (let i = 0; i < Math.max(d.length, l.length); i++) {
    const dv = d[i] ?? 0;
    const lv = l[i] ?? 0;
    if (dv > lv) return true;
    if (dv < lv) return false;
  }
  return false;
}

export const useVersion = () => {
  const { data: { isStaff = false } = {} } = useUser();
  const { data: config, isError: isConfigError } = useConfig();
  const { data: liveServerVersion, isError: isLiveVersionError } =
    useLiveServerVersion();

  const [ignoredWebUpdate, setIgnoredWebUpdate] = useState(false);
  const [isWebUpdateAvailable, setIsWebUpdateAvailable] = useState(false);

  useEffect(() => {
    if (hasWaitingServiceWorker()) {
      setIsWebUpdateAvailable(true);
    }

    const onUpdateAvailable = () => {
      setIgnoredWebUpdate(false);
      setIsWebUpdateAvailable(true);
    };

    window.addEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);

    return () => {
      window.removeEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);
    };
  }, []);

  // Poll /version endpoint every 5 minutes to detect new deployments.
  // When a newer version is detected, trigger a SW update check so the browser
  // downloads the new service-worker.js. We do NOT set isWebUpdateAvailable here —
  // the toast only shows when a new SW actually installs (SW_UPDATE_AVAILABLE_EVENT).
  // This prevents showing an "update available" toast that can't actually succeed.
  useEffect(() => {
    if (isWebUpdateAvailable) return;

    const VERSION_POLL_INTERVAL = 5 * 60 * 1000;

    const checkVersion = async () => {
      try {
        const res = await fetch("/version");
        if (!res.ok) return;
        const deployedVersion = (await res.text()).trim();
        if (isNewerVersion(deployedVersion, APP_VERSION)) {
          getSwRegistration()
            ?.update()
            .catch(() => {});
        }
      } catch {
        // Silently ignore — user may be offline
      }
    };

    checkVersion();
    const id = setInterval(checkVersion, VERSION_POLL_INTERVAL);
    return () => clearInterval(id);
  }, [isWebUpdateAvailable]);

  const configVersion = config?.version;
  const deployId = config?.deployId ?? configVersion ?? "";
  const hasVersionMismatch =
    typeof configVersion === "string" &&
    configVersion.length > 0 &&
    typeof liveServerVersion === "string" &&
    liveServerVersion.length > 0 &&
    configVersion !== liveServerVersion;

  return {
    localVersion: APP_VERSION,
    deployId,
    serverVersion: configVersion ?? liveServerVersion,
    configVersion: configVersion ?? null,
    liveServerVersion: liveServerVersion ?? null,
    webUpdate: isWebUpdateAvailable && !ignoredWebUpdate,
    serverUpdate:
      isStaff && (hasVersionMismatch || isConfigError || isLiveVersionError),
    setIgnored: () => setIgnoredWebUpdate(true),
    ignored: ignoredWebUpdate,
    isStaff,
    hasWaitingSW: hasWaitingServiceWorker(),
    isWebUpdateAvailable,
    resetWebUpdate: () => setIsWebUpdateAvailable(false),
  };
};
